// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { WorkFlowParametersDto } from '../../shared-workflow-module/_models/workflow-parameters-dto';
import { WFEWorkflowRawDto } from '../../shared-workflow-module/_models/wfe-workflow-raw-dto';
import { WFEBaseParametersDto } from '../../shared-workflow-module/_models/wfe-base-parameter-dto';
import { WFEVendorParametersDto } from '../_models/common/wfe-vendor-parameter-dto';
import { WorkflowHistoryResultDto } from '../../shared-workflow-module/_models/wfe-workflow-history-result-dto';
import { WFEVendorAccountParametersDto } from '../_models/workflow-data-entry/wfe-vendor-account-parameters-dto';
import { WFECreateCommunityVendorParametersDto } from '../_models/workflow-data-entry/wfe-create-community-vendor-parameters-dto';
import { StatusMessageResultDto } from '../../_models/status-message-result-dto';
import { WFEDeleteWorkflowParametersDto } from '../_models/common/wfe-delete-workflow-parameters-dto';
import { WFEReplaceInvoiceFileParametersDto } from '../_models/common/wfe-replace-invoice-file-parameters-dto';
import { WFEVendorTemplateResultDto } from '../_models/workflow-data-entry/wfe-vendor-template-result-dto';
import { WFEVendorAccountTemplateResultDto } from '../_models/workflow-data-entry/wfe-vendor-account-template-result-dto';
import { WFESendToVendorEditParametersDto } from '../_models/workflow-data-entry/wfe-send-to-vendor-edit-parameters-dto';
import { WFVendorNotesResultDto } from '../../shared-workflow-module/_models/wf-vendor-notes-result-dto';
import { WFEBankBalanceParameterDto } from '../_models/common/wfe-bank-balance-parameter-dto';
import { WFEBankBalanceResultDto } from '../_models/common/wfe-bank-balance-result-dto';
import { WorkFlowManageWorkOrderParametersDto } from '../_models/common/manage-work-order-parameters-dto';
import { WorkflowCommunityAttributeParameterDto } from '../_models/workflow-search/workflow-community-attribute-parameter-dto';
import { WfeVendorContractParametersDto } from '../_models/common/wfe-vendor-contract-parameters-dto';
import { WFEOwnerResultDto } from '../../shared-workflow-module/_models/wfe-owner-result-dto';
import { WFEVendorResultDto } from '../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { WFEVendorAccountResultDto } from '../../shared-workflow-module/_models/wfe-vendor-account-result-dto';
import { WFECommunityResultDto } from '../../shared-workflow-module/_models/wfe-community-result-dto';

// constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';

@Injectable({
    providedIn: 'root'
})
export class WorkflowEditorService {
    private readonly _subjectRefreshWorkflow: Subject<boolean> = new Subject();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    dataEntryEditor(baseParams: WorkFlowParametersDto): Observable<WFEWorkflowRawDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}wf/dataentry${baseParams.getQueryString()}`;

        return this.httpClient
            .get<WFEWorkflowRawDto>(url)
            .pipe(map((response) => plainToClass(WFEWorkflowRawDto, response)));
    }

    nextDataEntryEditor(): Observable<WFEWorkflowRawDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}next/dataentry`;

        return this.httpClient
            .get<WFEWorkflowRawDto>(url)
            .pipe(map((response) => plainToClass(WFEWorkflowRawDto, response)));
    }

    downloadWorkflowInvoice(baseParams: WFEBaseParametersDto): Observable<Blob> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}downloadinvoice${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);

        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData], { type: 'application/pdf' })));
    }

    getWorkflowEditorCommunities(baseParams: WFEBaseParametersDto): Observable<WFECommunityResultDto[]> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}communities${baseParams.getQueryString()}`;

        return this.httpClient
            .get<WFECommunityResultDto[]>(url)
            .pipe(map((response) => plainToClass(WFECommunityResultDto, response)));
    }

    getWorkflowEditorOwners(baseParams: WFEBaseParametersDto): Observable<WFEOwnerResultDto[]> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}owners${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.tenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEOwnerResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFEOwnerResultDto, response)));
    }

    getWorkflowEditorVendors(baseParams: WFEBaseParametersDto): Observable<WFEVendorResultDto[]> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendors${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.tenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEVendorResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFEVendorResultDto, response)));
    }

    getWorkflowVendorAccount(baseParams: WFEVendorParametersDto): Observable<WFEVendorAccountResultDto[]> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/accounts${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEVendorAccountResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFEVendorAccountResultDto, response)));
    }

    getWorkflowHistory(baseParams: WFEBaseParametersDto): Observable<WorkflowHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}wfhistory${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WorkflowHistoryResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WorkflowHistoryResultDto, response)));
    }

    closeWorkflowEditor(baseParams: WorkFlowParametersDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .put<StatusMessageResultDto>(`${URLConstants.ccWorkflowEditorBaseApiUrl}unlock`, classToPlain(baseParams))
            .pipe(map((responseData: any) => plainToClass(StatusMessageResultDto, responseData)));
    }

    getVendorNotes(baseParams: WFEVendorParametersDto): Observable<WFVendorNotesResultDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/notes${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFVendorNotesResultDto>(url, options)
            .pipe(map((response) => plainToClass(WFVendorNotesResultDto, response)));
    }

    createCommunityVendor(baseParams: WFECreateCommunityVendorParametersDto): Observable<any> {
        return this.httpClient
            .post<any>(`${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/communityvendor`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData));
    }

    transferWorkQueue(baseParams: WFEBaseParametersDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .post<StatusMessageResultDto>(
                `${URLConstants.ccWorkflowEditorBaseApiUrl}transferworkqueue`,
                classToPlain(baseParams)
            )
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    deleteWorkflow(baseParams: WFEDeleteWorkflowParametersDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .put<StatusMessageResultDto>(`${URLConstants.ccWorkflowEditorBaseApiUrl}delete`, classToPlain(baseParams))
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    replaceInvoice(baseParams: WFEReplaceInvoiceFileParametersDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .put<StatusMessageResultDto>(
                `${URLConstants.ccWorkflowEditorBaseApiUrl}replaceinvoice`,
                classToPlain(baseParams)
            )
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    getVendorLineTemplate(baseParams: WFEVendorParametersDto): Observable<WFEVendorTemplateResultDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/templates${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEVendorTemplateResultDto>(url, options)
            .pipe(map((response) => plainToClass(WFEVendorTemplateResultDto, response)));
    }

    getVendorAccountLineTemplate(
        baseParams: WFEVendorAccountParametersDto
    ): Observable<WFEVendorAccountTemplateResultDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/account/templates${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEVendorAccountTemplateResultDto>(url, options)
            .pipe(map((response) => plainToClass(WFEVendorAccountTemplateResultDto, response)));
    }

    sendWorkflowToVendorEdit(baseParams: WFESendToVendorEditParametersDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .put<StatusMessageResultDto>(
                `${URLConstants.ccWorkflowEditorBaseApiUrl}vendoredit`,
                classToPlain(baseParams)
            )
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    getWorkflowCommunityBillAddress(baseParams: WFEBaseParametersDto): Observable<string> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}billtoaddress${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient.get<string>(url, options).pipe(map((response: string) => response.toString()));
    }

    verifyAndSendWorkflowToSupervisor(baseParams: WFEWorkflowRawDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .put<StatusMessageResultDto>(
                `${URLConstants.ccWorkflowEditorBaseApiUrl}supervisor`,
                classToPlain(baseParams)
            )
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    verifyAndApproveWorkflow(baseParams: WFEWorkflowRawDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .put<StatusMessageResultDto>(
                `${URLConstants.ccWorkflowEditorBaseApiUrl}verifyapprove`,
                classToPlain(baseParams)
            )
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    getSubjectRefreshWorkflow(): Subject<boolean> {
        return this._subjectRefreshWorkflow;
    }

    updateSubjectRefreshWorkflow(value: boolean): void {
        this._subjectRefreshWorkflow.next(value);
    }

    getBankBalance(baseParams: WFEBankBalanceParameterDto): Observable<WFEBankBalanceResultDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}bankbalance${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url);

        return this.httpClient
            .get<WFEBankBalanceResultDto>(url)
            .pipe(map((response) => plainToClass(WFEBankBalanceResultDto, response)));
    }

    manageWorkOrders(baseParams: WorkFlowManageWorkOrderParametersDto, step: string): Observable<any> {
        return this.httpClient
            .post<any>(`${URLConstants.ccWorkflowEditorBaseApiUrl}${step}/workorders/manage`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData));
    }

    attributeCommunity(baseParams: WorkflowCommunityAttributeParameterDto): Observable<any> {
        return this.httpClient.put(
            `${URLConstants.ccWorkflowEditorBaseApiUrl}attribute-community`,
            classToPlain(baseParams)
        );
    }

    saveWorkflowVendorContracts(baseParams: WfeVendorContractParametersDto): Observable<any> {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/contracts`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData));
    }
}
